<template>
  <base-slide-over ref="locationSlideover" :title="isNew === true ? 'New Location' : 'Edit Location'" size="2xl" style="z-index: 10000">
    <base-spinner v-if="isLoading" />
    <base-form ref="locationForm" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid" v-else>
      <base-text-field label="Location name" placeholder="Enter location name" v-model="location.name" class="tw-mt-3" required :maxlength="255" />
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-3">
        <base-text-field class="tw-col-span-2" label="Line 1" placeholder="Enter line 1" v-model="location.line1" required />
        <base-text-field label="Line 2" placeholder="Enter line 2" v-model="location.line2" />
      </div>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-3">
        <base-text-field label="City" placeholder="Enter city" v-model="location.city" required />
        <base-select label="State" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="location.state" required />
        <base-text-field label="Zip Code" placeholder="Enter zip code" v-model="location.zip" required />
      </div>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-email-field label="Email" placeholder="Enter email" v-model="location.emailAddress" required />
        <base-phone-number-input label="Phone" placeholder="Enter phone" v-model="location.phoneNumber" :e164.sync="phone164" required />
      </div>
      <base-advanced-select class="tw-mt-3" label="Tax Rates (optional)" placeholder="Select tax rates" :options="businessTaxRates" v-model="value" label-key="name" multiple />
    </base-form>
    <inline-error id="location-error" :title="errorTitle" :message="errorMessage" :error="error" />
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" @click="$refs.locationSlideover.hide()">
          Cancel
        </base-button>
        <base-button :loading="isSaving" :disabled="!isFormValid" class="tw-w-auto" @click="save">
          Save
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapActions } from "vuex";
import InlineError from "@/components/InlineError";
import { STATE_OPTIONS } from "@/constants/constants";

export default {
  name: "BusinessLocationSlideover",
  components: {
    InlineError,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isSaving: false,
      isLoading: false,
      isFormValid: true,
      isNew: false,
      locationId: null,
      location: {},
      businessTaxRates: [],
      value: [],
      phone164: "",
      errorTitle: "",
      errorMessage: "",
      error: null,
      STATE_OPTIONS,
    };
  },
  methods: {
    ...mapActions("businessActivation", { businessActivationInit: "init" }),
    reset() {
      this.isSaving = false;
      this.isNew = false;
      this.locationId = null;
      this.location = {};
      this.$refs.locationForm.reset();
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
    },
    async show(locationId) {
      this.reset();

      this.locationId = locationId;
      this.value = null;

      if (!locationId) {
        this.isNew = true;
        await this.loadBusinessTaxRates();
        this.$refs.locationSlideover.show();
        return;
      }

      this.isLoading = true;
      await this.$axios
        .get(`/businesses/${this.businessId}/locations/${this.locationId}`)
        .then((res) => {
          this.location = res.data;
          this.value = res.data.taxRates;
          this.$refs.locationSlideover.show();
        })
        .catch((err) => {
          this.errorTitle = `Failed to load data for location`;
          this.errorMessage = `There was an error retrieving this location. Please try again.`;
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });

      await this.loadBusinessTaxRates();
    },
    async loadBusinessTaxRates() {
      await this.$axios.get(`/businesses/${this.businessId}/taxrates/`).then((res) => {
        this.businessTaxRates = res.data.filter((taxRate) => taxRate.active === true);
      });
    },
    close() {
      this.$refs.locationSlideover.hide();
    },
    save() {
      if (!this.$refs.locationForm.validate()) return;

      this.isSaving = true;

      this.location.taxRates = this.value;

      if (this.isNew) {
        this.$axios
          .post(`/businesses/${this.businessId}/locations`, {
            ...this.location,
            phoneNumber: this.phone164,
          })
          .then(() => {
            this.isSaving = false;
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Location Saved", message: this.location.name + " was created successfully" });
            this.businessActivationInit();
          })
          .catch((err) => {
            this.isSaving = false;
            this.errorTitle = `Failed to create ${this.location.name}`;
            this.errorMessage = `Location ${this.location.name} was not able to be created. Please try again.`;
            this.error = err;
          });
      } else {
        this.$axios
          .put(`/businesses/${this.businessId}/locations/${this.locationId}`, {
            ...this.location,
            phoneNumber: this.phone164,
          })
          .then(() => {
            this.isSaving = false;
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Location Updated", message: this.location.name + " was modified successfully" });
            this.businessActivationInit();
          })
          .catch((err) => {
            this.isSaving = false;
            this.errorTitle = `Failed to modify ${this.location.name}`;
            this.errorMessage = `Location ${this.location.name} was not able to be modified. Please try again.`;
            this.error = err;
          });
      }
    },
  },
};
</script>
